import { PaginatedResponse } from '@frontend/api-utils';

import { WorkflowType } from './workflow';
import { WorkflowFieldType } from './workflow-field-template';

export enum WorkflowConditionOperator {
    EQUALS = '==',
    NOT_EQUALS = '!=',
    GREATER_THAN_OR_EQUAL = '>=',
    LESS_THAN_OR_EQUAL = '<='
}

export enum TransactionWorkflowCondition {
    TRANSACTION_TRIGGER_SOURCE = 'transaction_trigger_source',
    TRANSACTION_FINAL_DESTINATION = 'transaction_final_destination'
}

export enum PackageWorkflowCondition {
    PACKAGE_TRIGGER_SOURCE = 'package_trigger_source',
    PACKAGE_FINAL_DESTINATION = 'package_final_destination',
    PACKAGE_PROOF_OF_IDENTITY_REQUIRED = 'package_proof_of_identity_required'
}

export enum ColliWorkflowCondition {
    COLLI_TRIGGER_SOURCE = 'colli_trigger_source',
    COLLI_FINAL_DESTINATION = 'colli_final_destination',
    COLLI_RECEIVING_POINT = 'colli_spot_receiving_point'
}

export enum EquipmentWorkflowCondition {
    EQUIPMENT_TRIGGER_SOURCE = 'equipment_trigger_source',
    EQUIPMENT_IN_STOCK = 'equipment_in_stock'
}

export enum OrderWorkflowCondition {
    ORDER_PRODUCT_ID = 'order_product_id'
}

export const WorkflowConditionType = {
    ...TransactionWorkflowCondition,
    ...PackageWorkflowCondition,
    ...ColliWorkflowCondition,
    ...EquipmentWorkflowCondition,
    ...OrderWorkflowCondition
} as const;

export type WorkflowConditionType = (typeof WorkflowConditionType)[keyof typeof WorkflowConditionType];

export type WorkflowConditionValue = string | boolean | number;
export type WorkflowConditionNullableValue = WorkflowConditionValue | null;

export interface WorkflowConditionBase<TType extends WorkflowConditionType = WorkflowConditionType> {
    id: string;
    type: TType;
    operator: WorkflowConditionOperator | null;
    value: WorkflowConditionNullableValue;
    account_id: string;
    workflow_id: string;
    step_id: string;
    creation_timestamp: string;
    update_timestamp: string;
    delete_timestamp?: string | null;
}

export type TransactionWorkflowConditionModel = WorkflowConditionBase<TransactionWorkflowCondition>;

export type PackageWorkflowConditionModel = WorkflowConditionBase<PackageWorkflowCondition>;

export type ColliWorkflowConditionModel = WorkflowConditionBase<ColliWorkflowCondition>;

export type EquipmentWorkflowConditionModel = WorkflowConditionBase<EquipmentWorkflowCondition>;

export type OrderWorkflowConditionModel = WorkflowConditionBase<OrderWorkflowCondition>;

export type WorkflowCondition =
    | TransactionWorkflowConditionModel
    | PackageWorkflowConditionModel
    | ColliWorkflowConditionModel
    | EquipmentWorkflowConditionModel
    | OrderWorkflowConditionModel;

export type WorkflowConditionListResponse = PaginatedResponse<WorkflowCondition>;

export type WorkflowConditionTemplateValueType = WorkflowFieldType | 'spot_type' | string;

export type WorkflowConditionTemplateResponse = Partial<Record<WorkflowType, Partial<Record<WorkflowConditionType, WorkflowConditionTemplateValueType>>>>;

export interface CreateWorkflowConditionBase<TType extends WorkflowConditionType = WorkflowConditionType> {
    type: TType;
    operator?: WorkflowConditionOperator | null;
    value?: WorkflowConditionNullableValue;
    update_timestamp?: string;
}

export type CreateTransactionWorkflowCondition = CreateWorkflowConditionBase<TransactionWorkflowCondition>;

export type CreatePackageWorkflowCondition = CreateWorkflowConditionBase<PackageWorkflowCondition>;

export type CreateColliWorkflowCondition = CreateWorkflowConditionBase<ColliWorkflowCondition>;

export type CreateEquipmentWorkflowCondition = CreateWorkflowConditionBase<EquipmentWorkflowCondition>;

export type CreateOrderWorkflowCondition = CreateWorkflowConditionBase<OrderWorkflowCondition>;

export type CreateWorkflowCondition =
    | CreateTransactionWorkflowCondition
    | CreatePackageWorkflowCondition
    | CreateColliWorkflowCondition
    | CreateEquipmentWorkflowCondition
    | CreateOrderWorkflowCondition;

export interface UpdateWorkflowConditionBase<TType extends WorkflowConditionType = WorkflowConditionType> {
    type?: TType | null;
    operator?: WorkflowConditionOperator | null;
    value?: WorkflowConditionNullableValue;
    null_fields?: string[] | null;
    update_timestamp?: string;
}

export type UpdateTransactionWorkflowCondition = UpdateWorkflowConditionBase<TransactionWorkflowCondition>;

export type UpdatePackageWorkflowCondition = UpdateWorkflowConditionBase<PackageWorkflowCondition>;

export type UpdateColliWorkflowCondition = UpdateWorkflowConditionBase<ColliWorkflowCondition>;

export type UpdateEquipmentWorkflowCondition = UpdateWorkflowConditionBase<EquipmentWorkflowCondition>;

export type UpdateOrderWorkflowCondition = UpdateWorkflowConditionBase<OrderWorkflowCondition>;

export type UpdateWorkflowCondition =
    | UpdateTransactionWorkflowCondition
    | UpdatePackageWorkflowCondition
    | UpdateColliWorkflowCondition
    | UpdateEquipmentWorkflowCondition
    | UpdateOrderWorkflowCondition;
